import i18n from '@/utils/i18n';
import { createStyles } from '@/contexts/theme-context';
import { type FC, ReactNode } from 'react';
import { getOrderSourceContact } from '@/screens/order-inbounds/utils';
import {
  InboundMetadataRowsProvider,
  useInboundMetadataRows,
} from './inbound-metadata-rows-context';
import { OrderMetadata } from '@/screens/order-inbounds/types';
import { StyleSheet, Text, View } from 'react-native';
import { TYPOGRAPHY } from '@/styles';

interface Props {
  children: ReactNode;
  orders: OrderMetadata[];
}

export const InboundMetadataRows: FC<Props> = ({ children, orders }) => {
  return (
    <InboundMetadataRowsProvider orders={orders}>
      <View style={styles.container}>{children}</View>
    </InboundMetadataRowsProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 4,
  },
});

export const InboundMetadataContactRow: React.FC = () => {
  const [styles] = useContactRowStyles();
  const { orders } = useInboundMetadataRows();

  const firstOrder = orders[0];
  if (!firstOrder) {
    return null;
  }

  const { uniqueEmployeeNames, mergeUUID, subject, source } = firstOrder;
  const isMergedOrder = Boolean(mergeUUID);
  const names =
    uniqueEmployeeNames.length > 0 ? uniqueEmployeeNames.join(', ') : null;
  const isEmail = subject || source === 'EMAIL';
  const contactTestIdSuffix = isMergedOrder
    ? '-merged'
    : isEmail
      ? '-email'
      : '-phone';
  const sourceContact =
    uniqueEmployeeNames.length > 1 ? null : getOrderSourceContact(firstOrder);
  let contactText: string | null = names
    ? `${names} • ${sourceContact}`
    : sourceContact;
  if (!sourceContact) {
    contactText = names;
  }

  return (
    <View style={[styles.line, styles.lineContact]}>
      {contactText ? (
        <Text
          numberOfLines={1}
          style={[TYPOGRAPHY.h4SemiBold, styles.textPrimary]}
          testID={`original-inbound-line${names ? '-name' : ''}${contactTestIdSuffix}`}
        >
          {contactText}
        </Text>
      ) : null}
    </View>
  );
};

const useContactRowStyles = createStyles((theme) => ({
  line: {
    flexDirection: 'row',
  },
  lineContact: {
    gap: 8,
    justifyContent: 'space-between',
  },
  textPrimary: {
    color: theme.sk.color.text.primary,
  },
}));

export const InboundMetadataDateRow: FC<{ showFirstDateOnly?: boolean }> = ({
  showFirstDateOnly,
}) => {
  const [styles] = useDateRowStyles();
  const { orders } = useInboundMetadataRows();

  const firstOrder = orders[0];
  if (!firstOrder) {
    return null;
  }

  const isMergedOrder = Boolean(firstOrder.mergeUUID);
  const date =
    isMergedOrder && !showFirstDateOnly
      ? i18n.t('multiple_dates')
      : firstOrder.fullDate;
  const lastDeliveryText = orders.find((o) => o.deliveryText)?.deliveryText;

  return (
    <View style={[styles.line, styles.lineDates]}>
      <Text
        numberOfLines={1}
        style={[TYPOGRAPHY.h4, styles.textSecondary, styles.textTime]}
        testID="original-inbound-line-time"
      >
        {date}
      </Text>
      {lastDeliveryText ? (
        <>
          <Text
            numberOfLines={1}
            style={[TYPOGRAPHY.h4, styles.textSecondary, styles.textSeparator]}
          >
            •
          </Text>
          <Text
            numberOfLines={1}
            style={[TYPOGRAPHY.h4, styles.textSecondary]}
            testID="original-inbound-line-delivery"
          >
            {lastDeliveryText}
          </Text>
        </>
      ) : null}
    </View>
  );
};

const useDateRowStyles = createStyles((theme) => ({
  line: {
    flexDirection: 'row',
  },
  lineDates: {
    justifyContent: 'flex-start',
  },
  textSecondary: {
    color: theme.sk.color.text.secondary,
  },
  textSeparator: {
    marginHorizontal: 4,
  },
  textTime: {
    flexShrink: 0,
  },
}));

export const InboundMetadataSubjectRow: FC = () => {
  const [styles] = useSubjectRowStyles();
  const { orders } = useInboundMetadataRows();

  const subject = orders[0]?.subject;
  if (!subject) {
    return null;
  }

  return (
    <View style={[styles.line, styles.lineSubject]}>
      <Text
        style={[TYPOGRAPHY.h4, styles.textSubject]}
        testID="original-inbound-line-subject"
      >
        {i18n.t('subject', { subject })}
      </Text>
    </View>
  );
};

const useSubjectRowStyles = createStyles((theme) => ({
  line: {
    flexDirection: 'row',
  },
  lineSubject: {
    justifyContent: 'flex-start',
  },
  textSubject: {
    color: theme.sk.color.text.secondary,
  },
}));
